import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { VirtualReference } from '../../utils/popper'
import useEventHandler from '../../../../../utils/hooks/useEventHandler'

export const STICKY_TRIGGER_THRESHOLD = 100
export const BOTTOM_DISAPPEAR_THRESHOLD = 70

/**
 * Hook that manages sticky virtual reference positioning for floating elements (currently used for RichTextEditor toolbar).
 *
 * Behavior:
 * - Virtual reference positioned above the element
 * - Sticky: Virtual reference positioned at top of viewport (triggered at 100px from top)
 * - Hidden: Virtual reference removed when element is out of view or below 70px from bottom
 *
 * @param elementRef - Reference to the element
 * @param shouldBeActive - Whether the toolbar should be active
 */
export default function useStickyVirtualReference(
  elementRef: RefObject<HTMLDivElement | null>,
  shouldBeActive: boolean,
) {
  const [isSticky, setIsSticky] = useState(false)
  const [virtualReferenceElement, setVirtualReferenceElement] = useState<VirtualReference | null>(null)
  const cleanupRef = useRef<number | null>(null)
  const prevClientRect = useRef<DOMRect | null>(null)

  const cancelFrame = useCallback(() => {
    if (cleanupRef.current) {
      cancelAnimationFrame(cleanupRef.current)
      cleanupRef.current = null
    }
  }, [])

  const calculatePosition = useCallback(() => {
    if (!shouldBeActive || !elementRef.current) {
      setVirtualReferenceElement(null)
      setIsSticky(false)
      prevClientRect.current = null
      return
    }

    const rect = elementRef.current.getBoundingClientRect()

    // Hide if container is out of view
    if (rect.top >= window.innerHeight || rect.bottom <= BOTTOM_DISAPPEAR_THRESHOLD) {
      setVirtualReferenceElement(null)
      prevClientRect.current = null
      return
    }

    const shouldBeSticky = rect.top <= STICKY_TRIGGER_THRESHOLD
    setIsSticky(shouldBeSticky)

    const virtualTop = shouldBeSticky ? 0 : rect.top
    const prev = prevClientRect.current

    // Only create new VirtualReference if position actually changed
    if (prev === null || prev.left !== rect.left || prev.top !== virtualTop || prev.width !== rect.width) {
      const newClientRect = new DOMRect(rect.left, virtualTop, rect.width, 0)

      prevClientRect.current = newClientRect

      setVirtualReferenceElement(
        new VirtualReference({
          clientRect: newClientRect,
        }),
      )
    }
  }, [elementRef, shouldBeActive])

  const handlePositionUpdate = useCallback(() => {
    cancelFrame()
    cleanupRef.current = requestAnimationFrame(calculatePosition)
  }, [calculatePosition, cancelFrame])

  // Only register events when toolbar is active
  useEventHandler('scroll', handlePositionUpdate, document, {
    passive: true,
    capture: true,
    disabled: !shouldBeActive,
  })

  useEventHandler('resize', handlePositionUpdate, window, {
    passive: true,
    disabled: !shouldBeActive,
  })

  useEffect(() => {
    if (shouldBeActive) calculatePosition()
    return () => cancelFrame()
  }, [shouldBeActive, calculatePosition, cancelFrame])

  return [virtualReferenceElement, isSticky]
}
